import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import {
  GraduationCap,
  Plus,
  Play,
  Edit,
  Trash2,
  Video,
  FileText,
  Link as LinkIcon,
  Clock,
  Target,
  CheckCircle,
  X,
  ExternalLink,
  MapPin,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Safety', 'Operations', 'Equipment', 'Emergency', 'Orientation', 'Compliance'];
const DIFFICULTY_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];
const CONTENT_TYPES = ['Video', 'Document', 'Link', 'Interactive'];

const getContentIcon = (type) => {
  switch (type) {
    case 'Video': return <Video className="h-4 w-4" />;
    case 'Document': return <FileText className="h-4 w-4" />;
    case 'Link': return <LinkIcon className="h-4 w-4" />;
    default: return <GraduationCap className="h-4 w-4" />;
  }
};

const getDifficultyColor = (level) => {
  switch (level) {
    case 'Beginner': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'Intermediate': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'Advanced': return 'bg-red-500/20 text-red-400 border-red-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

// Video embed helper - extracts video ID and creates embed URL
const getEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  return url; // Return as-is if not recognized
};

export function TrainingModulesPanel({
  isOpen,
  onClose,
  currentTour,
  currentSweep,
  cameraPosition,
  onNavigate,
}) {
  const { canEdit } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Operations',
    difficulty_level: 'Beginner',
    duration_minutes: '',
    content_type: 'Video',
    content_url: '',
    embed_video: true,
    learning_objectives: [],
    sweep_id: '',
    rotation: null,
    zoom: null,
  });

  const token = localStorage.getItem('zentara_token');

  const fetchModules = useCallback(async () => {
    if (!currentTour?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training-modules?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setModules(data);
      }
    } catch (err) {
      console.error('Failed to fetch training modules:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchModules();
    }
  }, [isOpen, fetchModules]);

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        tour_id: currentTour.id,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
      };
      
      const res = await fetch(`${API_URL}/api/training-modules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        fetchModules();
        setIsCreateDialogOpen(false);
        resetForm();
      }
    } catch (err) {
      console.error('Failed to create training module:', err);
    }
  };

  const handleUpdate = async () => {
    if (!selectedModule) return;
    try {
      const payload = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
      };
      
      const res = await fetch(`${API_URL}/api/training-modules/${selectedModule.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        fetchModules();
        setIsEditDialogOpen(false);
        setSelectedModule(null);
        resetForm();
      }
    } catch (err) {
      console.error('Failed to update training module:', err);
    }
  };

  const handleDelete = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this training module?')) return;
    try {
      const res = await fetch(`${API_URL}/api/training-modules/${moduleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchModules();
      }
    } catch (err) {
      console.error('Failed to delete training module:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'Operations',
      difficulty_level: 'Beginner',
      duration_minutes: '',
      content_type: 'Video',
      content_url: '',
      embed_video: true,
      learning_objectives: [],
      sweep_id: '',
      rotation: null,
      zoom: null,
    });
  };

  const captureCurrentPosition = () => {
    if (currentSweep?.id) {
      setFormData(prev => ({
        ...prev,
        sweep_id: currentSweep.id,
        rotation: cameraPosition?.rotation || null,
        zoom: cameraPosition?.zoom || null,
      }));
    }
  };

  const openEditDialog = (module) => {
    setSelectedModule(module);
    setFormData({
      title: module.title || '',
      description: module.description || '',
      category: module.category || 'Operations',
      difficulty_level: module.difficulty_level || 'Beginner',
      duration_minutes: module.duration_minutes?.toString() || '',
      content_type: module.content_type || 'Video',
      content_url: module.content_url || '',
      embed_video: module.embed_video ?? true,
      learning_objectives: module.learning_objectives || [],
      sweep_id: module.sweep_id || '',
      rotation: module.rotation || null,
      zoom: module.zoom || null,
    });
    setIsEditDialogOpen(true);
  };

  const openViewDialog = (module) => {
    setSelectedModule(module);
    setIsViewDialogOpen(true);
  };

  const navigateToModule = (module) => {
    if (module.sweep_id && onNavigate) {
      onNavigate(module.sweep_id, { 
        rotation: module.rotation,
        zoom: module.zoom 
      });
    }
  };

  const filteredModules = activeTab === 'all' 
    ? modules 
    : modules.filter(m => m.category === activeTab);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-primary" />
            Training Modules
          </DialogTitle>
          <DialogDescription>
            Learning content and training materials for this tour
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Category Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start overflow-x-auto">
              <TabsTrigger value="all">All</TabsTrigger>
              {CATEGORIES.map(cat => (
                <TabsTrigger key={cat} value={cat}>{cat}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Module List */}
          <ScrollArea className="flex-1 mt-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredModules.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No training modules found
              </div>
            ) : (
              <div className="grid gap-4 pr-4">
                {filteredModules.map(module => (
                  <Card key={module.id} className="hover:border-primary/50 transition-colors">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {getContentIcon(module.content_type)}
                          <CardTitle className="text-lg">{module.title}</CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={getDifficultyColor(module.difficulty_level)}>
                            {module.difficulty_level}
                          </Badge>
                          <Badge variant="outline">{module.category}</Badge>
                        </div>
                      </div>
                      {module.description && (
                        <CardDescription className="mt-1">{module.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {module.duration_minutes && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {module.duration_minutes} min
                            </span>
                          )}
                          {module.sweep_id && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              Location set
                            </span>
                          )}
                          {module.learning_objectives?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Target className="h-3 w-3" />
                              {module.learning_objectives.length} objectives
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {module.sweep_id && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigateToModule(module)}
                            >
                              <MapPin className="h-4 w-4 mr-1" />
                              Go to
                            </Button>
                          )}
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => openViewDialog(module)}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Start
                          </Button>
                          {canEdit && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon-sm"
                                onClick={() => openEditDialog(module)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon-sm"
                                onClick={() => handleDelete(module.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Footer with Add Button */}
        {canEdit && (
          <DialogFooter>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Training Module
            </Button>
          </DialogFooter>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
            setSelectedModule(null);
            resetForm();
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {isEditDialogOpen ? 'Edit Training Module' : 'Create Training Module'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Module title"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this module"
                  />
                </div>

                <div>
                  <Label>Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Difficulty</Label>
                  <Select
                    value={formData.difficulty_level}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, difficulty_level: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DIFFICULTY_LEVELS.map(level => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Content Type</Label>
                  <Select
                    value={formData.content_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, content_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e.target.value }))}
                    placeholder="e.g., 15"
                  />
                </div>

                <div className="col-span-2">
                  <Label>Content URL (YouTube, Vimeo, or document link)</Label>
                  <Input
                    value={formData.content_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, content_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>

                <div className="col-span-2 flex items-center justify-between">
                  <div>
                    <Label>Embed Video in 3D View</Label>
                    <p className="text-xs text-muted-foreground">Show video embedded in the tour (YouTube/Vimeo)</p>
                  </div>
                  <Switch
                    checked={formData.embed_video}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, embed_video: checked }))}
                  />
                </div>

                <div className="col-span-2">
                  <Label>Location in Tour</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={formData.sweep_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, sweep_id: e.target.value }))}
                      placeholder="Sweep ID (optional)"
                      className="flex-1"
                    />
                    <Button variant="outline" onClick={captureCurrentPosition}>
                      <MapPin className="h-4 w-4 mr-1" />
                      Capture Current
                    </Button>
                  </div>
                  {formData.sweep_id && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Position captured {formData.rotation ? '+ rotation' : ''} {formData.zoom ? '+ zoom' : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsCreateDialogOpen(false);
                setIsEditDialogOpen(false);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button onClick={isEditDialogOpen ? handleUpdate : handleCreate}>
                {isEditDialogOpen ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View/Play Module Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setIsViewDialogOpen(false);
            setSelectedModule(null);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  {getContentIcon(selectedModule?.content_type)}
                  {selectedModule?.title}
                </DialogTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={getDifficultyColor(selectedModule?.difficulty_level)}>
                    {selectedModule?.difficulty_level}
                  </Badge>
                  <Badge variant="outline">{selectedModule?.category}</Badge>
                </div>
              </div>
              {selectedModule?.description && (
                <DialogDescription>{selectedModule.description}</DialogDescription>
              )}
            </DialogHeader>

            {/* Video Embed */}
            {selectedModule?.content_url && selectedModule?.embed_video && (
              <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
                <iframe
                  src={getEmbedUrl(selectedModule.content_url)}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedModule.title}
                />
              </div>
            )}

            {/* External Link (if not embedded) */}
            {selectedModule?.content_url && !selectedModule?.embed_video && (
              <div className="p-4 bg-muted rounded-lg">
                <a 
                  href={selectedModule.content_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open content in new tab
                </a>
              </div>
            )}

            {/* Learning Objectives */}
            {selectedModule?.learning_objectives?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Learning Objectives
                </h4>
                <ul className="space-y-1">
                  {selectedModule.learning_objectives.map((obj, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {obj}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <DialogFooter>
              {selectedModule?.sweep_id && (
                <Button variant="outline" onClick={() => {
                  navigateToModule(selectedModule);
                  setIsViewDialogOpen(false);
                }}>
                  <MapPin className="h-4 w-4 mr-2" />
                  Go to Location
                </Button>
              )}
              <Button onClick={() => setIsViewDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default TrainingModulesPanel;
